/**
 * Step 2: Passagers et Options
 * - Nombre de passagers avec champs simplifiés (un champ texte par passager)
 * - Bagages
 * - Options (siège bébé, panneau accueil, etc.)
 * - Instructions spéciales
 */
import React, { useState, useEffect } from 'react';
import { useBooking } from '../../context/BookingContext';
import { bookingAPI } from '../../services/bookingApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Users, Briefcase, Baby, 
  FileText, Droplets, ArrowRight, ArrowLeft,
  Plus, Minus, CheckCircle2, User, Clock
} from 'lucide-react';

const Step2Details = () => {
  const { bookingData, updateBookingData, nextStep, prevStep } = useBooking();
  const [options, setOptions] = useState([]);
  // Simplified: one text field per passenger (name, phone, notes combined)
  const [passengerNotes, setPassengerNotes] = useState(
    bookingData.passenger_notes || Array(bookingData.passengers || 1).fill('')
  );

  useEffect(() => {
    bookingAPI.getOptions().then(data => {
      setOptions(data.options || []);
    }).catch(console.error);
  }, []);

  // Sync passenger fields with passenger count
  useEffect(() => {
    const count = bookingData.passengers || 1;
    setPassengerNotes(prev => {
      const newNotes = [...prev];
      while (newNotes.length < count) newNotes.push('');
      return newNotes.slice(0, count);
    });
  }, [bookingData.passengers]);

  // Sync with booking data
  useEffect(() => {
    updateBookingData({ passenger_notes: passengerNotes });
  }, [passengerNotes]);

  const updatePassengerNote = (index, value) => {
    const newNotes = [...passengerNotes];
    newNotes[index] = value;
    setPassengerNotes(newNotes);
  };

  const optionIcons = {
    child_seat: Baby,
    welcome_sign: FileText,
    water_bottle: Droplets,
    extra_wait: Clock
  };

  const toggleOption = (optionValue) => {
    const current = bookingData.options || [];
    if (current.includes(optionValue)) {
      updateBookingData({ options: current.filter(o => o !== optionValue) });
    } else {
      updateBookingData({ options: [...current, optionValue] });
    }
  };

  const updateCounter = (field, delta, min = 0, max = 10) => {
    const current = bookingData[field] || 0;
    const newValue = Math.max(min, Math.min(max, current + delta));
    updateBookingData({ [field]: newValue });
  };

  const getVehicleCapacity = () => {
    const vehicleCapacities = {
      berline: { passengers: 3, luggage: 3 },
      suv: { passengers: 4, luggage: 4 },
      van: { passengers: 7, luggage: 8 },
      minibus: { passengers: 8, luggage: 10 }
    };
    return vehicleCapacities[bookingData.vehicle_category] || { passengers: 3, luggage: 3 };
  };

  const capacity = getVehicleCapacity();

  return (
    <div className="space-y-6" data-testid="booking-step-2">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
          Passagers et options
        </h2>
        <p className="text-[#E5E5E5]/60 text-sm">
          Personnalisez votre course
        </p>
      </div>

      {/* Passengers Counter */}
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-[#1F1F1F] rounded-lg">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-[#C9A24D]" />
            <div>
              <span className="text-[#E5E5E5]">Passagers</span>
              <p className="text-xs text-[#E5E5E5]/40">Max {capacity.passengers} pour ce véhicule</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => updateCounter('passengers', -1, 1, capacity.passengers)}
              className="border-[#C9A24D]/30 text-[#C9A24D] h-8 w-8"
              data-testid="passengers-minus"
            >
              <Minus className="w-3 h-3" />
            </Button>
            <span className="text-lg font-semibold text-[#E5E5E5] w-8 text-center" data-testid="passengers-count">
              {bookingData.passengers}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => updateCounter('passengers', 1, 1, capacity.passengers)}
              disabled={bookingData.passengers >= capacity.passengers}
              className="border-[#C9A24D]/30 text-[#C9A24D] h-8 w-8 disabled:opacity-50"
              data-testid="passengers-plus"
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Passenger Fields - Simplified (one field per passenger) */}
        {bookingData.passengers > 0 && (
          <div className="space-y-3 p-4 bg-[#1F1F1F]/50 rounded-lg">
            <Label className="text-[#E5E5E5] flex items-center gap-2">
              <User className="w-4 h-4 text-[#C9A24D]" />
              Informations passagers
              <span className="text-[#E5E5E5]/40 text-xs">(facultatif)</span>
            </Label>
            <div className="space-y-2">
              {passengerNotes.map((note, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <div className="w-6 h-8 flex items-center justify-center text-[#C9A24D] text-sm font-medium">
                    {index + 1}.
                  </div>
                  <Input
                    type="text"
                    value={note}
                    onChange={(e) => updatePassengerNote(index, e.target.value)}
                    placeholder="Nom, téléphone, notes..."
                    className="flex-1 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] h-10"
                    data-testid={`passenger-${index}-note`}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-[#E5E5E5]/40">
              Indiquez le nom et téléphone de chaque passager si nécessaire
            </p>
          </div>
        )}
      </div>

      {/* Luggage */}
      <div className="flex items-center justify-between p-4 bg-[#1F1F1F] rounded-lg">
        <div className="flex items-center gap-3">
          <Briefcase className="w-5 h-5 text-[#C9A24D]" />
          <div>
            <span className="text-[#E5E5E5]">Bagages</span>
            <p className="text-xs text-[#E5E5E5]/40">Max {capacity.luggage} pour ce véhicule</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => updateCounter('luggage', -1, 0, capacity.luggage)}
            className="border-[#C9A24D]/30 text-[#C9A24D] h-8 w-8"
            data-testid="luggage-minus"
          >
            <Minus className="w-3 h-3" />
          </Button>
          <span className="text-lg font-semibold text-[#E5E5E5] w-8 text-center" data-testid="luggage-count">
            {bookingData.luggage}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => updateCounter('luggage', 1, 0, capacity.luggage)}
            disabled={bookingData.luggage >= capacity.luggage}
            className="border-[#C9A24D]/30 text-[#C9A24D] h-8 w-8 disabled:opacity-50"
            data-testid="luggage-plus"
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Options */}
      {options.length > 0 && (
        <div className="space-y-3">
          <Label className="text-[#E5E5E5]">Options</Label>
          <div className="grid grid-cols-1 gap-3">
            {options.map((option) => {
              const Icon = optionIcons[option.value] || CheckCircle2;
              const isSelected = (bookingData.options || []).includes(option.value);
              
              return (
                <div
                  key={option.value}
                  onClick={() => toggleOption(option.value)}
                  className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-[#C9A24D]/10 border border-[#C9A24D]'
                      : 'bg-[#1F1F1F] border border-[#C9A24D]/10 hover:border-[#C9A24D]/30'
                  }`}
                  data-testid={`option-${option.value}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-[#C9A24D]' : 'text-[#E5E5E5]/60'}`} />
                    <div>
                      <span className="text-[#E5E5E5]">{option.name}</span>
                      {option.description && (
                        <p className="text-xs text-[#E5E5E5]/40">{option.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#C9A24D] font-medium">+{option.price}€</span>
                    {isSelected && <CheckCircle2 className="w-5 h-5 text-[#C9A24D]" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Special Requests */}
      <div className="space-y-2">
        <Label className="text-[#E5E5E5]">Instructions spéciales</Label>
        <Textarea
          value={bookingData.special_requests || ''}
          onChange={(e) => updateBookingData({ special_requests: e.target.value })}
          placeholder="Ex: Besoin d'aide pour les bagages, point de rencontre spécifique..."
          className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D] min-h-[100px]"
          data-testid="special-requests"
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={prevStep}
          className="flex-1 border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10"
          data-testid="step2-prev-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button
          onClick={nextStep}
          className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
          data-testid="step2-next-button"
        >
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default Step2Details;
